import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { I18N_KEYS } from '@/language/keys';
import { Flex, FlexCenVer, FlexCol } from '@/UI/styles';
import { Input } from '@/UI/Input/Input';
import { TextArea } from '@/UI/TextArea/TextArea';
import { Dropdown } from '@/UI/Dropdown/Dropdown';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { PlusIcon } from '@/UI/Icons/PlusIcon';
import { CheckMarkIcon } from '@/UI/Icons/CheckMarkIcon';
import type { CommunityCategory } from '@/api/workflowCommunityPublishing.api';

export const NAME_MAX = 120;
export const DESCRIPTION_MAX = 1000;
export const TAGS_MAX = 5;
const TAG_SKELETON_WIDTHS = [80, 120, 90, 70, 100];

interface FieldLabelRowProps {
  label: string;
  counter?: string;
  required?: boolean;
  requiredError?: boolean;
  htmlFor?: string;
}

export const FieldLabelRow = ({
  label,
  counter,
  required = false,
  requiredError = false,
  htmlFor,
}: FieldLabelRowProps) => (
  <FlexCenVer sx={{ justifyContent: 'space-between', mb: 1 }}>
    <TextSmRg color={token.textSecondary} component={htmlFor ? 'label' : undefined} htmlFor={htmlFor}>
      {label}
      {required && (
        <span aria-hidden style={requiredError ? { color: token.textDanger } : undefined}>
          {' *'}
        </span>
      )}
    </TextSmRg>
    {counter && <TextSmRg color={token.textSecondary}>{counter}</TextSmRg>}
  </FlexCenVer>
);

interface PublishToCommunityModalDescriptionProps {
  name: string;
  onNameChange: (v: string) => void;
  nameError: string | null;
  description: string;
  onDescriptionChange: (v: string) => void;
  descriptionError: string | null;
  category: string;
  onCategoryChange: (v: string) => void;
  categoryError: string | null;
  categories: CommunityCategory[];
  categoriesLoading: boolean;
  categoryRequired?: boolean;
  tags: string[];
  onToggleTag: (tag: string) => void;
  isPublishingAsTool?: boolean;
  isOrgPublish?: boolean;
  orgName?: string;
}

const PublishToCommunityModalDescription = ({
  name,
  onNameChange,
  nameError,
  description,
  onDescriptionChange,
  descriptionError,
  category,
  onCategoryChange,
  categoryError,
  categories,
  categoriesLoading,
  categoryRequired = true,
  tags,
  onToggleTag,
  isPublishingAsTool = false,
  isOrgPublish = false,
  orgName,
}: PublishToCommunityModalDescriptionProps) => {
  const { t } = useTranslation();
  const categoryId = useId();
  const categoryOptions = useMemo(
    () => categories.map((c) => ({ id: c.id, label: c.title, value: c.id })),
    [categories],
  );
  const availableTags = useMemo(() => categories.find((c) => c.id === category)?.tags ?? [], [categories, category]);
  const showTagsSection = categoriesLoading || availableTags.length > 0;

  return (
    <>
      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.NAME_LABEL)}
          counter={`${name.length}/${NAME_MAX}`}
          required
          requiredError={Boolean(nameError)}
        />
        <Input
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          size="large"
          fullWidth
          error={Boolean(nameError)}
        />
        {nameError && (
          <TextSmRg color={token.textDanger} sx={{ mt: 0.5 }}>
            {nameError}
          </TextSmRg>
        )}
      </FlexCol>

      <FlexCol>
        <FieldLabelRow
          label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_LABEL)}
          counter={`${description.length}/${DESCRIPTION_MAX}`}
          required
          requiredError={Boolean(descriptionError)}
        />
        <TextArea
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          aria-label={t(
            I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_LABEL,
          )}
          placeholder={getDescriptionPlaceholder(t, { isPublishingAsTool, orgName })}
          rows={3}
          error={Boolean(descriptionError)}
          inputProps={{ style: { resize: 'none' } }}
          sx={{ mt: 0 }}
        />
        {descriptionError && (
          <TextSmRg color={token.textDanger} sx={{ mt: 0.5 }}>
            {descriptionError}
          </TextSmRg>
        )}
      </FlexCol>

      {!isOrgPublish && (
        <FlexCol>
          <FieldLabelRow
            label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.CATEGORY_LABEL)}
            required={categoryRequired}
            requiredError={Boolean(categoryError)}
            htmlFor={categoryId}
          />
          <Dropdown
            id={categoryId}
            value={category}
            options={categoryOptions}
            onChange={(opt) => onCategoryChange(opt.value)}
            emptyState={t(
              I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.CATEGORY_PLACEHOLDER,
            )}
            width="100%"
            matchTriggerWidth
            sx={{ height: 32 }}
          />
          {categoryError && (
            <TextSmRg color={token.textDanger} sx={{ mt: 0.5 }}>
              {categoryError}
            </TextSmRg>
          )}
        </FlexCol>
      )}

      {!isOrgPublish && showTagsSection && (
        <FlexCol>
          <FieldLabelRow
            label={t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.TAGS_LABEL)}
          />
          <Flex sx={{ flexWrap: 'wrap', gap: 1 }}>
            {categoriesLoading
              ? TAG_SKELETON_WIDTHS.map((width, i) => (
                  <Skeleton key={i} variant="rounded" width={width} height={26} sx={{ borderRadius: 1 }} />
                ))
              : availableTags.map((tag) => {
                  const isSelected = tags.includes(tag.id);
                  const atLimit = tags.length >= TAGS_MAX && !isSelected;
                  return (
                    <FlexCenVer
                      key={tag.id}
                      onClick={() => onToggleTag(tag.id)}
                      sx={{
                        border: `1px solid ${isSelected ? 'transparent' : token.border}`,
                        borderRadius: 1,
                        pr: 1,
                        background: isSelected ? themeColor.white100 : token.bg,
                        color: isSelected ? color.Black92 : token.text,
                        cursor: atLimit ? 'default' : 'pointer',
                        opacity: atLimit ? 0.5 : 1,
                        '&:hover': {
                          background: isSelected ? themeColor.white100 : atLimit ? token.bg : token.bgTransparent,
                        },
                      }}
                    >
                      <FlexCenVer sx={{ width: 24, height: 24, justifyContent: 'center' }}>
                        {isSelected ? (
                          <CheckMarkIcon width={12} height={12} />
                        ) : (
                          <PlusIcon width={12} height={12} strokeWidth={2} />
                        )}
                      </FlexCenVer>
                      <TextSmRg color="inherit">{tag.text}</TextSmRg>
                    </FlexCenVer>
                  );
                })}
          </Flex>
        </FlexCol>
      )}
    </>
  );
};

function getDescriptionPlaceholder(
  t: ReturnType<typeof useTranslation>['t'],
  { isPublishingAsTool, orgName }: { isPublishingAsTool: boolean; orgName?: string },
): string {
  if (orgName) {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.DESCRIPTION_PLACEHOLDER, {
      orgName,
    });
  }
  if (isPublishingAsTool) {
    return t(
      I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_PLACEHOLDER_TOOL,
    );
  }
  return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.MODAL.DESCRIPTION_STEP.DESCRIPTION_PLACEHOLDER);
}

export default PublishToCommunityModalDescription;
